import { z } from 'zod';

/**
 * A product form — the shapes the one recipe arrives in (the Round, the
 * cubes, the flour). Deliberately separate from Recipe: a recipe is
 * something you cook, a product is something you buy.
 */
export const ProductSchema = z
  .object({
    id: z.string().min(1, 'id is required'),
    name: z.string().min(1, 'name is required'),
    /** One-line form descriptor shown under the name, e.g. "Firm and sliceable, wrapped in paper". */
    form: z.string().min(1, 'form is required'),
    /** Drives the badge treatment. 'coming' renders the honest not-yet state. */
    status: z.enum(['available', 'coming']),
    /** Badge text. Kept separate from `status` so the wording can change
     *  without touching the styling logic. */
    statusLabel: z.string().min(1, 'statusLabel is required'),
    /** Optional photo path — must start with '/' so it resolves from public/.
     *  Absent means "no honest image exists yet"; the card renders a
     *  typographic placeholder rather than borrowing a misleading one. */
    photo: z.string().regex(/^\//, 'photo path must start with /').optional(),
    /** Required whenever `photo` is set — enforced by the refine below. */
    alt: z.string().min(1).optional(),
    description: z.string().min(1, 'description is required'),
    /** Short "what you do with it" lines under the description. */
    notes: z.array(z.string()).min(1, 'at least one note'),
  })
  .refine((p) => !p.photo || Boolean(p.alt), {
    message: 'alt text is required whenever photo is set',
    path: ['alt'],
  });

export type Product = z.infer<typeof ProductSchema>;
export const ProductsSchema = z.array(ProductSchema);
